import { Box, Grid2 } from "@mui/material";
import { ActionIconsV2, DataGrid, Modal, Tabs } from "../common";
import { searchObjects, theme } from "../../utils";
import { useAtom } from "jotai";
import {
  selectedOrgIdAtom,
  toastOptionsAtom,
  userPermissionAtom,
  washTypeDataGridPaginationAtom,
  washTypeFilterStateAtom,
} from "../../atoms";
import { GridColDef, GridSortDirection, GridSortModel } from "@mui/x-data-grid";
import { ChangeEvent, useEffect, useState } from "react";
import {
  useHasPermission,
  UserPermission,
} from "@zdistancelab-packages/ui-permission-validator";
import { Action, ChargeType, Module } from "../../enums";
import { WashTypeForm, WashTypeToolbar } from "./fragments";
import {
  useCreateWashTypeMutation,
  useDeleteWashTypeMutation,
  useDeleteWashTypesMutation,
  useFetchMiscWashTypesQuery,
  useFetchWashTypesQuery,
  useUpdateWashTypeMutation,
} from "../../api";
import { TabItem, WashType } from "../../types";
import { WashTypeModel } from "../../models";
import { useSearchParams } from "react-router-dom";

export const WashTypes = () => {
  const [filterValues, setFilterValues] = useAtom(washTypeFilterStateAtom);
  const [sortFilters, setSortFilters] = useState({
    sortBy: "",
    sortOrder: "",
  });
  const [washTypes, setWashTypes] = useState<WashType[]>([]);
  const [filteredWashTypes, setFilteredWashTypes] = useState<WashType[]>([]);
  const [miscWashTypes, setMiscWashTypes] = useState<WashType[]>([]);
  const [filteredMiscWashTypes, setFilteredMiscWashTypes] = useState<
    WashType[]
  >([]);
  const [adjustmentWashTypes, setAdjustmentWashTypes] = useState<WashType[]>(
    [],
  );
  const [filteredAdjustmentWashTypes, setFilteredAdjustmentWashTypes] =
    useState<WashType[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedTab, setSelectedTab] = useState<number>(0);
  const [isCreateWashTypeModalOpen, setIsCreateWashTypeModalOpen] =
    useState(false);
  const [selectedOrgId] = useAtom(selectedOrgIdAtom);
  const [userPermission] = useAtom<UserPermission>(userPermissionAtom);
  const [washTypePaginationModel, setWashTypePaginationModel] = useAtom(
    washTypeDataGridPaginationAtom,
  );
  const [, setToastOptions] = useAtom(toastOptionsAtom);
  const [selectedWashTypeIds, setSelectedWashTypeIds] = useState<number[]>([]);
  const [queryParams, setQueryParams] = useSearchParams();
  const hasWritePermission = useHasPermission(userPermission, [
    { module: Module.User, action: Action.Write },
  ]);
  const hasDeletePermission = useHasPermission(userPermission, [
    { module: Module.User, action: Action.Delete },
  ]);
  const fetchWashTypesQuery = useFetchWashTypesQuery(
    selectedOrgId,
    selectedOrgId ? true : false,
  );
  const fetchMiscWashTypesQuery = useFetchMiscWashTypesQuery(selectedOrgId, {
    enabled: selectedOrgId ? true : false,
  });
  const createWashTypeMutation = useCreateWashTypeMutation();
  const updateWashTypeMutation = useUpdateWashTypeMutation();
  const deleteWashTypeMutation = useDeleteWashTypeMutation();
  const deleteWashTypesMutation = useDeleteWashTypesMutation();

  function handleOpenCreateWashTypeModal(): void {
    setIsCreateWashTypeModalOpen(true);
  }

  const handleSortModelChange = (sortModel: GridSortModel) => {
    if (sortModel.length > 0) {
      const sortField = sortModel[0].field;
      const sortOrder: GridSortDirection = sortModel[0].sort;
      if (sortOrder) {
        setSortFilters({
          ...sortFilters,
          sortBy: sortField,
          sortOrder: sortOrder,
        });
      }
    } else if (sortFilters.sortBy !== "") {
      setSortFilters({
        ...sortFilters,
        sortBy: "",
        sortOrder: "",
      });
    }
  };

  const handleCreateWashType = (
    data: Omit<WashType, "id" | "miscellaneous">,
  ) => {
    createWashTypeMutation.mutate({
      name: data.name,
      description: data.description,
      unitOfMeasurement: data.unitOfMeasurement,
      code: data.code,
      orgId: data.orgId,
      rate: data.rate,
      miscellaneous: selectedTab === 0 ? false : true,
      chargeType:
        selectedTab === 0
          ? ChargeType.Wash
          : data.chargeType
            ? data.chargeType
            : ChargeType.Miscellaneous,
    });
    setIsCreateWashTypeModalOpen(false);
  };

  const handleEditWashTypes = (data: Omit<WashType, "miscellaneous">) => {
    updateWashTypeMutation.mutate({
      id: data.id,
      name: data.name,
      description: data.description,
      unitOfMeasurement: data.unitOfMeasurement,
      code: data.code,
      orgId: data.orgId,
      rate: data.rate,
      miscellaneous: selectedTab === 0 ? false : true,
      chargeType:
        selectedTab === 0
          ? ChargeType.Wash
          : data.chargeType
            ? data.chargeType
            : ChargeType.Miscellaneous,
    });
  };

  const handleDeleteWashType = (data: Pick<WashType, "id" | "orgId">) => {
    deleteWashTypeMutation.mutate({
      id: data.id,
      orgId: data.orgId,
    });
  };

  const handleDeleteWashTypes = () => {
    deleteWashTypesMutation.mutate({
      orgId: selectedOrgId,
      ids: selectedWashTypeIds,
    });
  };

  const handleApplyFilters = () => {
    setFilteredWashTypes(getSearchedWashTypes(washTypes));
    setFilteredMiscWashTypes(getSearchedWashTypes(miscWashTypes));
    setFilteredAdjustmentWashTypes(getSearchedWashTypes(adjustmentWashTypes));
  };

  const handleSearch = (e: ChangeEvent<HTMLInputElement>) => {
    resetPage();
    setSearchTerm(e.target.value.toLowerCase());
  };

  const handleTabChange = (index: number) => {
    resetPage();
    setSelectedTab(index);
  };

  const handleCloseCreateWashTypeModal = () => {
    setIsCreateWashTypeModalOpen(false);
  };

  const handleRowSelectionChange = (rows: WashType[]) => {
    setSelectedWashTypeIds(rows.map((e) => e.id));
  };

  const getSearchedWashTypes = (washType: WashType[]) => {
    const searched = searchObjects(washType, [], searchTerm);
    return searched;
  };

  const resetPage = () => {
    setWashTypePaginationModel({ ...washTypePaginationModel, page: 0 });
  };

  const getRows = (): WashType[] => {
    switch (selectedTab) {
      case 0:
        return filteredWashTypes;
      case 1:
        return filteredMiscWashTypes;
      default:
        return filteredAdjustmentWashTypes;
    }
  };

  const getTabName = () => {
    switch (selectedTab) {
      case 0:
        return "washTypes";
      case 1:
        return "miscWashTypes";
      default:
        return "adjustments";
    }
  };

  const getAddWashTypeLabel = () => {
    switch (selectedTab) {
      case 0:
        return "Add Wash Type";
      case 1:
        return "Add Misc Wash Type";
      default:
        return "Add Adjustment";
    }
  };

  const getEditWashTypeLabel = () => {
    switch (selectedTab) {
      case 0:
        return "Edit Wash Type";
      case 1:
        return "Edit Misc Wash Type";
      default:
        return "Edit Adjustment";
    }
  };

  const getDeleteWashTypeLabel = () => {
    switch (selectedTab) {
      case 0:
        return "Delete Wash Type";
      case 1:
        return "Delete Misc Wash Type";
      default:
        return "Delete Adjustment";
    }
  };

  const tabItems: TabItem[] = [
    {
      key: "Wash Types",
      value: 0,
    },
    {
      key: "Misc Wash Types",
      value: 1,
    },
    {
      key: "Adjustments",
      value: 2,
    },
  ];

  const columns: GridColDef[] = [
    {
      field: "code",
      headerName: "Code",
      width: 200,
      editable: false,
    },
    {
      field: "name",
      headerName: "Name",
      width: 150,
      editable: false,
    },
    {
      field: "description",
      headerName: "Description",
      headerAlign: "center",
      align: "center",
      width: 200,
      editable: false,
      sortable: false,
    },
    {
      field: "unitOfMeasurement",
      headerName: "Unit of measurement",
      headerAlign: "center",
      align: "center",
      width: 250,
      editable: false,
    },
    {
      field: "rate",
      headerName: "Default Rate",
      headerAlign: "center",
      align: "center",
      width: 150,
      editable: false,
      valueFormatter: (value) => {
        return `$ ${parseFloat(value).toFixed(2)}`;
      },
    },
  ];

  if (hasWritePermission || hasDeletePermission) {
    columns.push({
      field: "actions",
      headerName: "Actions",
      width: 150,
      editable: false,
      sortable: false,
      renderCell: (params) => {
        return (
          <ActionIconsV2
            onEdit={handleEditWashTypes}
            onDelete={handleDeleteWashType}
            rowData={params.row}
            formProps={{
              form: WashTypeForm,
              name: "editWashTypeForm",
              extraProps: {
                isCreateMode: false,
                isAdjustments: selectedTab === 2,
              },
            }}
            editProps={{
              title: getEditWashTypeLabel(),
              primaryButtonText: "Update",
            }}
            deleteProps={{
              title: getDeleteWashTypeLabel(),
            }}
          />
        );
      },
    });
  }

  useEffect(() => {
    const queryFilters = {
      orgId: queryParams.get("orgId")
        ? Number(queryParams.get("orgId"))
        : undefined,
      page: queryParams.get("page")
        ? Number(queryParams.get("page"))
        : undefined,
      pageSize: queryParams.get("pageSize")
        ? Number(queryParams.get("pageSize"))
        : undefined,
      sortBy: queryParams.get("sortBy") || undefined,
      sortOrder: queryParams.get("sortOrder") || undefined,
      tab: queryParams.get("tab") ? queryParams.get("tab") : "washTypes",
    };

    switch (queryFilters.tab) {
      case "washTypes":
        setSelectedTab(0);
        break;
      case "miscWashTypes":
        setSelectedTab(1);
        break;
      default:
        setSelectedTab(2);
    }

    const tempWashTypeFiltersFromQueryStrings = Object.entries(
      queryFilters,
    ).reduce((acc: any, [key, value]) => {
      if (value) {
        acc[key] = value;
      }
      return acc;
    }, {});

    const tempFilterValuesFromQueryStrings = Object.entries({
      orgId: tempWashTypeFiltersFromQueryStrings.orgId,
    }).reduce((acc: any, [key, value]) => {
      if (value) {
        acc[key] = value;
      }
      return acc;
    }, {});

    Object.values(tempFilterValuesFromQueryStrings).length > 0 &&
      setFilterValues({
        ...filterValues,
        ...tempFilterValuesFromQueryStrings,
      });
    setWashTypePaginationModel({
      ...washTypePaginationModel,
      page: tempWashTypeFiltersFromQueryStrings.page || 0,
      pageSize: tempWashTypeFiltersFromQueryStrings.pageSize || 10,
    });
    setSortFilters({
      ...sortFilters,
      sortBy: tempWashTypeFiltersFromQueryStrings.sortBy || sortFilters.sortBy,
      sortOrder:
        tempWashTypeFiltersFromQueryStrings.sortOrder || sortFilters.sortOrder,
    });
  }, []);

  useEffect(() => {
    const filters = Object.entries(filterValues).reduce(
      (acc: any, [key, value]) => {
        if (value) {
          acc[key] = value;
        }
        return acc;
      },
      {},
    );

    const paginationfilters = Object.entries(washTypePaginationModel).reduce(
      (acc: any, [key, value]) => {
        if (value) {
          acc[key] = value;
        }
        return acc;
      },
      {},
    );

    const sortingFilters = Object.entries(sortFilters).reduce(
      (acc: any, [key, value]) => {
        if (value) {
          acc[key] = value;
        }
        return acc;
      },
      {},
    );

    setQueryParams({
      ...filters,
      ...paginationfilters,
      ...sortingFilters,
      tab: getTabName(),
    });
  }, [filterValues, washTypePaginationModel, sortFilters]);

  useEffect(() => {
    setFilteredWashTypes(getSearchedWashTypes(washTypes));
    setFilteredMiscWashTypes(getSearchedWashTypes(miscWashTypes));
    setFilteredAdjustmentWashTypes(getSearchedWashTypes(adjustmentWashTypes));
  }, [washTypes, miscWashTypes]);

  useEffect(() => {
    setFilteredWashTypes(getSearchedWashTypes(washTypes));
    setFilteredMiscWashTypes(getSearchedWashTypes(miscWashTypes));
    setFilteredAdjustmentWashTypes(getSearchedWashTypes(adjustmentWashTypes));
  }, [searchTerm]);

  useEffect(() => {
    if (fetchWashTypesQuery.isSuccess) {
      const washTypes = WashTypeModel.buildWashType(
        fetchWashTypesQuery.data,
        selectedOrgId,
        false,
      );
      setWashTypes(washTypes);
    }

    if (fetchWashTypesQuery.isError) {
      setToastOptions({
        open: true,
        message: "Something went wrong",
        severity: "error",
      });
      setWashTypes([]);
    }
  }, [
    fetchWashTypesQuery.isSuccess,
    fetchWashTypesQuery.data,
    fetchWashTypesQuery.isError,
    fetchWashTypesQuery.isLoading,
    fetchWashTypesQuery.isFetching,
    fetchWashTypesQuery.isFetched,
    fetchWashTypesQuery.isRefetching,
  ]);

  useEffect(() => {
    if (fetchMiscWashTypesQuery.isSuccess) {
      const miscWashTypes = WashTypeModel.buildWashType(
        fetchMiscWashTypesQuery.data.filter(
          (item) => item.chargeType === ChargeType.Miscellaneous,
        ),
        selectedOrgId,
        true,
      );

      const adjustmentTypes = WashTypeModel.buildWashType(
        fetchMiscWashTypesQuery.data.filter(
          (item) => item.chargeType !== ChargeType.Miscellaneous,
        ),
        selectedOrgId,
        true,
      );

      setMiscWashTypes(miscWashTypes);
      setAdjustmentWashTypes(adjustmentTypes);
    }

    if (fetchMiscWashTypesQuery.isError) {
      setToastOptions({
        open: true,
        message: "Something went wrong",
        severity: "error",
      });
      setMiscWashTypes([]);
    }
  }, [
    fetchMiscWashTypesQuery.isSuccess,
    fetchMiscWashTypesQuery.data,
    fetchMiscWashTypesQuery.isError,
    fetchMiscWashTypesQuery.isLoading,
    fetchMiscWashTypesQuery.isFetching,
    fetchMiscWashTypesQuery.isFetched,
    fetchMiscWashTypesQuery.isRefetching,
  ]);

  useEffect(() => {
    if (createWashTypeMutation.isSuccess) {
      fetchWashTypesQuery.refetch();
      fetchMiscWashTypesQuery.refetch();
      setToastOptions({
        open: true,
        message: "Wash type created successfully",
        severity: "success",
      });
    }

    if (createWashTypeMutation.isError) {
      setToastOptions({
        open: true,
        message: "Something went wrong",
        severity: "error",
      });
    }

    if (createWashTypeMutation.isError) {
      if (createWashTypeMutation.error.status === 409) {
        setToastOptions({
          open: true,
          message:
            createWashTypeMutation.error.response?.data.error.message ||
            "Something went wrong",
          severity: "error",
        });
      } else {
        setToastOptions({
          open: true,
          message: "Something went wrong",
          severity: "error",
        });
      }
    }
  }, [
    createWashTypeMutation.isSuccess,
    createWashTypeMutation.data,
    createWashTypeMutation.isError,
    createWashTypeMutation.isLoading,
  ]);

  useEffect(() => {
    if (updateWashTypeMutation.isSuccess) {
      fetchWashTypesQuery.refetch();
      fetchMiscWashTypesQuery.refetch();
      setToastOptions({
        open: true,
        message: "Wash type updated successfully",
        severity: "success",
      });
    }

    if (updateWashTypeMutation.isError) {
      setToastOptions({
        open: true,
        message: "Something went wrong",
        severity: "error",
      });
    }

    if (updateWashTypeMutation.isError) {
      if (updateWashTypeMutation.error.status === 409) {
        setToastOptions({
          open: true,
          message:
            updateWashTypeMutation.error.response?.data.error.message ||
            "Something went wrong",
          severity: "error",
        });
      } else {
        setToastOptions({
          open: true,
          message: "Something went wrong",
          severity: "error",
        });
      }
    }
  }, [
    updateWashTypeMutation.isSuccess,
    updateWashTypeMutation.data,
    updateWashTypeMutation.isError,
    updateWashTypeMutation.isLoading,
  ]);

  useEffect(() => {
    if (deleteWashTypeMutation.isSuccess) {
      fetchWashTypesQuery.refetch();
      fetchMiscWashTypesQuery.refetch();
      setToastOptions({
        open: true,
        message: "Wash type deleted successfully",
        severity: "success",
      });
    }

    if (deleteWashTypeMutation.isError) {
      if (deleteWashTypeMutation.error.status === 400) {
        const accounts =
          deleteWashTypeMutation.error.response?.data?.error?.accounts?.join(
            ", ",
          ) || "";

        setToastOptions({
          open: true,
          message: `Cannot delete wash type. This wash type is currently used by the following accounts: ${accounts}`,
          severity: "error",
        });
      } else {
        setToastOptions({
          open: true,
          message: "Something went wrong",
          severity: "error",
        });
      }
    }
  }, [
    deleteWashTypeMutation.isSuccess,
    deleteWashTypeMutation.data,
    deleteWashTypeMutation.isError,
    deleteWashTypeMutation.isLoading,
  ]);
  useEffect(() => {
    if (deleteWashTypesMutation.isSuccess) {
      fetchWashTypesQuery.refetch();
      fetchMiscWashTypesQuery.refetch();

      const { deletedCount, blockedCount } = deleteWashTypesMutation.data.data;

      if (deletedCount > 0 && blockedCount === 0) {
        setToastOptions({
          open: true,
          message: "Wash types deleted successfully",
          severity: "success",
        });
      } else if (deletedCount > 0 && blockedCount > 0) {
        setToastOptions({
          open: true,
          message:
            "Some wash types cannot be deleted as they are associated with accounts.",
          severity: "error",
        });
      } else if (deletedCount === 0 && blockedCount > 0) {
        setToastOptions({
          open: true,
          message:
            "Wash types cannot be deleted as they are associated with accounts.",
          severity: "error",
        });
      }
    }

    if (deleteWashTypesMutation.isError) {
      setToastOptions({
        open: true,
        message: "Something went wrong",
        severity: "error",
      });
    }
  }, [
    deleteWashTypesMutation.isSuccess,
    deleteWashTypesMutation.data,
    deleteWashTypesMutation.isError,
    deleteWashTypesMutation.isLoading,
  ]);

  useEffect(() => {
    const filters = Object.entries(filterValues).reduce(
      (acc: any, [key, value]) => {
        if (value) {
          acc[key] = value;
        }
        return acc;
      },
      {},
    );
    const paginationfilters = Object.entries(washTypePaginationModel).reduce(
      (acc: any, [key, value]) => {
        if (value) {
          acc[key] = value;
        }
        return acc;
      },
      {},
    );
    const sortingFilters = Object.entries(sortFilters).reduce(
      (acc: any, [key, value]) => {
        if (value) {
          acc[key] = value;
        }
        return acc;
      },
      {},
    );
    setQueryParams({
      ...filters,
      ...paginationfilters,
      ...sortingFilters,
      tab: getTabName(),
    });
  }, [
    selectedTab,
    filteredWashTypes,
    filteredMiscWashTypes,
    filteredAdjustmentWashTypes,
  ]);

  return (
    <>
      <Box
        sx={{
          marginTop: "50px",
          width: "100%",
          boxShadow: "0px 0px 4px rgba(0, 0, 0, 0.25)",
          borderRadius: "10px",
        }}
      >
        <Box>
          <Grid2
            container
            direction={"column"}
            spacing={2}
            sx={{
              backgroundColor: theme.palette.secondary.light,
              padding: "20px",
              paddingTop: "0px",
              paddingBottom: "0px",
              borderTopLeftRadius: "10px",
              borderTopRightRadius: "10px",
              borderBottom: "1px solid #E5E5E5",
            }}
          >
            <Tabs
              tabItems={tabItems}
              onTabChange={handleTabChange}
              value={selectedTab}
            />
          </Grid2>
          <Grid2
            container
            direction={"column"}
            sx={{
              marginTop: "0px",
              marginBottom: "8px",
              backgroundColor: "#f5f5f5",
              borderRadius: "10px",
            }}
          >
            <WashTypeToolbar
              onApplyFilters={handleApplyFilters}
              handleSearch={handleSearch}
              resetPage={resetPage}
            />
          </Grid2>
          <Grid2 container direction={"column"} spacing={2}>
            <DataGrid
              columns={columns}
              rows={getRows()}
              loading={
                fetchWashTypesQuery.isLoading ||
                fetchWashTypesQuery.isRefetching ||
                fetchMiscWashTypesQuery.isLoading ||
                fetchMiscWashTypesQuery.isRefetching ||
                createWashTypeMutation.isLoading ||
                updateWashTypeMutation.isLoading ||
                deleteWashTypeMutation.isLoading ||
                deleteWashTypesMutation.isLoading
              }
              sortModel={[
                {
                  field: sortFilters.sortBy || "",
                  sort: (sortFilters.sortOrder as GridSortDirection) || null,
                },
              ]}
              onSortModelChange={handleSortModelChange}
              paginationModel={washTypePaginationModel}
              setPaginationModel={setWashTypePaginationModel}
              onSelectedRowsChange={handleRowSelectionChange}
              sx={{
                border: "none",
                backgroundColor: theme.palette.secondary.light,
                padding: "20px",
                paddingTop: "0px",
                "& .MuiSvgIcon-root": {
                  fontSize: 16,
                },
                borderBottomLeftRadius: "10px",
                borderBottomRightRadius: "10px",
                height: "70vh",
              }}
              footerProps={{
                addBtnLabel: hasWritePermission ? getAddWashTypeLabel() : "",
                hasAddIcon: true,
                hasRemoveIcon: true,
                removeBtnLabel: hasDeletePermission ? "Delete Selected" : "",
                onAddButtonClick: handleOpenCreateWashTypeModal,
                handleConfirmRemove: handleDeleteWashTypes,
                disableRemoveBtn: selectedWashTypeIds.length > 0 ? false : true,
                confirmationModalTitle: "Delete Wash Types",
                confirmationModalMessage:
                  selectedWashTypeIds.length == 1
                    ? "Are you sure you want to delete this wash type?"
                    : `Are you sure you want to delete these ${selectedWashTypeIds.length} wash types?`,
              }}
            />
          </Grid2>
        </Box>
      </Box>
      <Modal
        open={isCreateWashTypeModalOpen}
        onClose={handleCloseCreateWashTypeModal}
        title={getAddWashTypeLabel()}
        content={
          <WashTypeForm
            id={"createWashTypeForm"}
            isCreateMode={true}
            isAdjustments={selectedTab === 2 ? true : false}
            handleEdit={handleCreateWashType}
            defaultValues={
              { orgId: selectedOrgId } as Omit<WashType, "miscellaneous">
            }
          ></WashTypeForm>
        }
        form={"createWashTypeForm"}
        primaryButtonText="Save"
        secondaryButtonText="Cancel"
        onSecondaryButtonClick={handleCloseCreateWashTypeModal}
        sx={{
          modal: {
            width: { lg: "30%", xs: "80%", sm: "50%" },
            maxHeight: "90vh",
            display: "flex",
            flexDirection: "column",
          },
          content: {
            overflowY: "auto",
            flex: 1,
          },
        }}
      />
    </>
  );
};
